import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, Zap, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';

// Submits the site's indexable URLs to Bing/Yandex via the IndexNow protocol.
// The verification key file is served from /<key>.txt on the site root.
const KEY_FILE_URL = 'https://motionreality.co.uk/21fe51819494cb6ef7edfe09519bc1bc.txt';

export default function IndexNowPanel() {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const res = await base44.functions.invoke('notifyIndexNow', {});
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-[#111115] border border-white/10 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Zap className="w-5 h-5 text-primary" />
          <h2 className="text-white font-bold text-lg uppercase tracking-tight">IndexNow</h2>
        </div>
        <p className="text-white/50 text-sm leading-relaxed mb-5 max-w-2xl">
          Instantly notifies Bing and Yandex (and other IndexNow partners) when your pages change so they
          re-crawl updated content quickly — typically within minutes rather than weeks. Submitting pushes
          your core pages, all published products, blog articles and showcase rigs in a single batch.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-primary text-white px-5 py-3 text-xs font-bold tracking-widest hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {submitting ? 'SUBMITTING…' : 'SUBMIT SITE TO INDEXNOW'}
          </button>
          <a
            href={KEY_FILE_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-white/40 hover:text-white text-xs font-bold tracking-wider"
          >
            KEY FILE <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {error && (
          <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 p-4">
            <XCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        )}

        {result && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 p-4">
            <div className="flex items-start gap-2">
              {result.ok ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              )}
              <div className="text-sm">
                <p className="font-bold text-white">
                  {result.ok
                    ? `${result.count} URL${result.count === 1 ? '' : 's'} submitted to IndexNow`
                    : result.status === 429
                      ? 'IndexNow rate-limited this submission (429)'
                      : `IndexNow responded with status ${result.status}`}
                </p>
                <p className="text-white/50 mt-1">
                  {result.ok
                    ? 'Bing/Yandex will re-crawl these pages shortly.'
                    : result.status === 429
                      ? 'You have submitted recently and IndexNow is throttling further requests. Wait a while before re-submitting — your URLs are already in the crawl queue.'
                      : 'A non-success status usually means the key file is unreachable or the key does not match. Open the key file link to verify it loads.'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}